import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BookOpen, 
  Calendar, 
  User, 
  Clock, 
  MapPin, 
  Building,
  Wrench,
  Fuel,
  Filter,
  ChevronDown,
  ChevronUp,
  Tractor,
  Home,
  Truck,
  Search,
  Download,
  Edit,
  Trash2,
  X,
  Check,
  FileText,
  Euro,
  Eye,
  CircleDot,
  Ban,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { FicheInterventionModal } from '../components/interventions/FicheInterventionModal';

// Configuration des statuts de facturation - 4 états
const STATUTS_FACTURATION = {
  facture: { label: 'Facturé', color: 'bg-green-100 text-green-700', icon: Check, emoji: '🟢' },
  a_facturer: { label: 'À facturer', color: 'bg-amber-100 text-amber-700', icon: Clock, emoji: '🟡' },
  non_facture: { label: 'Non facturé', color: 'bg-gray-100 text-gray-500', icon: CircleDot, emoji: '⚪' },
  pas_de_facture: { label: 'Pas de facture', color: 'bg-red-100 text-red-700', icon: Ban, emoji: '🔴' }
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JournalInterventionsPage() {
  const { activeSociete } = useWorkspace();
  const [interventions, setInterventions] = useState([]);
  const [consommations, setConsommations] = useState([]);
  const [stats, setStats] = useState(null);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  
  // Modal fiche détaillée
  const [selectedInterventionId, setSelectedInterventionId] = useState(null);
  const [showFicheModal, setShowFicheModal] = useState(false);
  
  // Filtres avancés
  const [filters, setFilters] = useState({
    dateDebut: '',
    dateFin: '',
    employe: '',
    typeActivite: 'all',
    client: '',
    parcelle: '',
    operation: '',
    materiel: '',
    lieu: '',
    recherche: ''
  });
  
  // Listes pour les filtres
  const [clientsList, setClientsList] = useState([]);
  const [parcellesList, setParcellesList] = useState([]);
  const [operationsList, setOperationsList] = useState([]);
  const [materielsList, setMaterielsList] = useState([]);
  const [lieuxList] = useState([
    'Atelier', 'Hangar', 'Dépôt', 'Bureau', 'Route/Transport', 
    'Champ', 'Exploitation', 'Client', 'Autre'
  ]);
  
  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    if (activeSociete?.id) {
      fetchJournal();
      fetchEmployes();
      fetchFilterOptions();
    }
  }, [activeSociete?.id]);

  const fetchJournal = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.dateDebut) params.append('date_debut', filters.dateDebut);
      if (filters.dateFin) params.append('date_fin', filters.dateFin);
      if (filters.employe) params.append('employe_id', filters.employe);
      if (filters.typeActivite !== 'all') params.append('type_activite', filters.typeActivite);
      if (filters.client) params.append('client', filters.client);
      if (filters.operation) params.append('operation', filters.operation);
      if (filters.materiel) params.append('materiel', filters.materiel);
      if (filters.lieu) params.append('lieu', filters.lieu);
      if (filters.recherche) params.append('recherche', filters.recherche);
      params.append('limit', '500');

      const response = await fetch(
        `${API_URL}/api/bot-interventions/journal?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        let results = data.interventions || [];
        
        // Filtre parcelle côté client (structure complexe)
        if (filters.parcelle) {
          results = results.filter(i => 
            i.parcelles?.some(p => p.nom?.toLowerCase().includes(filters.parcelle.toLowerCase()))
          );
        }
        if (filters.lieu) {
          results = results.filter(i => 
            (i.lieu || '').toLowerCase().includes(filters.lieu.toLowerCase())
          );
        }
        if (filters.recherche) {
          const search = filters.recherche.toLowerCase();
          results = results.filter(i => 
            (i.client_nom || '').toLowerCase().includes(search) ||
            (i.operation || '').toLowerCase().includes(search) ||
            (i.materiel || '').toLowerCase().includes(search) ||
            (i.lieu || '').toLowerCase().includes(search) ||
            (i.employe_nom || '').toLowerCase().includes(search) ||
            (i.commentaire || '').toLowerCase().includes(search) ||
            (i.description || '').toLowerCase().includes(search) ||
            i.parcelles?.some(p => p.nom?.toLowerCase().includes(search))
          );
        }
        
        setInterventions(results);
        setConsommations(data.consommations || []);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching journal:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployes = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/employes`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setEmployes(data.employes || []);
      }
    } catch (error) {
      console.error('Error fetching employes:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Récupérer les options pour les filtres depuis les interventions existantes
      const response = await fetch(
        `${API_URL}/api/bot-interventions/journal?limit=1000`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        const ints = data.interventions || [];
        
        // Extraire les valeurs uniques
        const clients = [...new Set(ints.map(i => i.client_nom).filter(Boolean))];
        const parcelles = [...new Set(ints.flatMap(i => (i.parcelles || []).map(p => p.nom)).filter(Boolean))];
        const operations = [...new Set(ints.map(i => i.operation).filter(Boolean))];
        const materiels = [...new Set(ints.map(i => i.materiel).filter(Boolean))];
        
        setClientsList(clients.sort());
        setParcellesList(parcelles.sort());
        setOperationsList(operations.sort());
        setMaterielsList(materiels.sort());
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchJournal();
  };

  const clearFilters = () => {
    setFilters({
      dateDebut: '',
      dateFin: '',
      employe: '',
      typeActivite: 'all',
      client: '',
      parcelle: '',
      operation: '',
      materiel: '',
      lieu: '',
      recherche: ''
    });
    setTimeout(() => fetchJournal(), 100);
  };

  const exportCSV = () => {
    const headers = ['Date', 'Employé', 'Type', 'Client', 'Parcelles', 'Opération', 'Lieu', 'Matériel', 'Heures', 'Commentaire'];
    const rows = interventions.map(i => [
      i.date || '',
      i.employe_nom || '',
      i.type_activite || 'agricole',
      i.client_nom || '',
      (i.parcelles || []).map(p => p.nom).join('; '),
      i.operation || '',
      i.lieu || '',
      i.materiel || '',
      i.heures_travaillees || 0,
      (i.commentaire || i.description || '').replace(/"/g, '""')
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `journal_interventions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const startEdit = (intervention) => {
    setEditingId(intervention.id);
    setEditData({ ...intervention });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/tarifs-eta/interventions/${editingId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          },
          body: JSON.stringify(editData)
        }
      );
      
      if (response.ok) {
        setEditingId(null);
        setEditData({});
        fetchJournal();
      }
    } catch (error) {
      console.error('Error saving edit:', error);
    }
  };

  // Toggle rapide du statut Facturé - Cycle entre les 4 états
  const toggleFacture = async (intervention, e) => {
    e?.stopPropagation(); // Empêcher l'ouverture de la fiche
    
    // Déterminer le statut actuel
    const currentStatut = intervention.statut_facturation || 
      (intervention.facture ? 'facture' : 
       intervention.pas_de_facture || intervention.non_facturable ? 'pas_de_facture' :
       intervention.a_facturer ? 'a_facturer' : 'non_facture');
    
    // Cycle: non_facture -> a_facturer -> facture -> non_facture
    // (pas_de_facture est à part, accessible via menu)
    const cycleStatuts = ['non_facture', 'a_facturer', 'facture'];
    const currentIndex = cycleStatuts.indexOf(currentStatut);
    const nextStatut = currentStatut === 'pas_de_facture' 
      ? 'non_facture' 
      : cycleStatuts[(currentIndex + 1) % cycleStatuts.length];
    
    // Mise à jour optimiste de l'UI
    setInterventions(prev => prev.map(i => 
      i.id === intervention.id ? { 
        ...i, 
        statut_facturation: nextStatut,
        facture: nextStatut === 'facture',
        a_facturer: nextStatut === 'a_facturer',
        non_facturable: nextStatut === 'pas_de_facture',
        pas_de_facture: nextStatut === 'pas_de_facture'
      } : i
    ));
    
    try {
      const response = await fetch(
        `${API_URL}/api/bot-interventions/intervention/${intervention.id}/statut-facturation?statut=${nextStatut}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          }
        }
      );
      
      if (!response.ok) {
        // Rollback si erreur
        setInterventions(prev => prev.map(i => 
          i.id === intervention.id ? intervention : i
        ));
      }
    } catch (error) {
      console.error('Error toggling facture:', error);
      // Rollback
      setInterventions(prev => prev.map(i => 
        i.id === intervention.id ? intervention : i
      ));
    }
  };

  // Marquer comme "Pas de facture" (interne)
  const setNonFacturable = async (intervention, e) => {
    e?.stopPropagation();
    
    setInterventions(prev => prev.map(i => 
      i.id === intervention.id ? { 
        ...i, 
        statut_facturation: 'pas_de_facture',
        facture: false,
        a_facturer: false,
        non_facturable: true,
        pas_de_facture: true
      } : i
    ));
    
    try {
      await fetch(
        `${API_URL}/api/bot-interventions/intervention/${intervention.id}/statut-facturation?statut=pas_de_facture`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'X-Societe-Id': activeSociete?.id
          }
        }
      );
    } catch (error) {
      console.error('Error setting non-facturable:', error);
    }
  };

  // Ouvrir la fiche d'intervention détaillée
  const openFicheIntervention = (intervention) => {
    setSelectedInterventionId(intervention.id);
    setShowFicheModal(true);
  };

  // Callback après mise à jour de la fiche
  const handleFicheUpdate = () => {
    fetchJournal(); // Recharger le journal
  };

  // Obtenir le statut de facturation normalisé
  const getStatutFacturation = (intervention) => {
    if (intervention.statut_facturation && STATUTS_FACTURATION[intervention.statut_facturation]) {
      return intervention.statut_facturation;
    }
    if (intervention.facture) return 'facture';
    if (intervention.pas_de_facture || intervention.non_facturable) return 'pas_de_facture';
    if (intervention.a_facturer) return 'a_facturer';
    return 'non_facture';
  };

  const getActivityIcon = (intervention) => {
    const type = intervention.type_activite || 'agricole';
    const lieu = (intervention.lieu || '').toLowerCase();
    const operation = (intervention.operation || '').toLowerCase();
    
    if (type === 'interne') {
      if (lieu.includes('atelier') || operation.includes('mecanique')) return <Wrench className="h-4 w-4" />;
      if (lieu.includes('hangar') || lieu.includes('depot') || lieu.includes('dépôt')) return <Home className="h-4 w-4" />;
      if (lieu.includes('transport') || lieu.includes('route')) return <Truck className="h-4 w-4" />;
      if (lieu.includes('bureau')) return <FileText className="h-4 w-4" />;
      return <Building className="h-4 w-4" />;
    }
    return <Tractor className="h-4 w-4" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const totalHeuresFiltrees = interventions.reduce((sum, i) => sum + (i.heures_travaillees || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="journal-interventions-page">
      {/* Header */}
      <div className="mb-4 flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-green-600" />
            Journal des Interventions
          </h1>
          <p className="text-gray-600">Historique complet des activités - {activeSociete?.nom || 'Toutes exploitations'}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCSV} variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-900">{interventions.length}</p>
                <p className="text-xs text-blue-600">Interventions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-900">{totalHeuresFiltrees.toFixed(1)}h</p>
                <p className="text-xs text-green-600">Heures totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* À FACTURER - Important pour la secrétaire */}
        <Card className={`bg-gradient-to-br ${
          interventions.filter(i => !i.facture && i.type_activite !== 'interne').length > 0 
            ? 'from-red-50 to-red-100 border-red-300 ring-2 ring-red-200' 
            : 'from-gray-50 to-gray-100 border-gray-200'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                interventions.filter(i => !i.facture && i.type_activite !== 'interne').length > 0 
                  ? 'bg-red-500 animate-pulse' 
                  : 'bg-gray-400'
              }`}>
                <Euro className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${
                  interventions.filter(i => !i.facture && i.type_activite !== 'interne').length > 0 
                    ? 'text-red-700' 
                    : 'text-gray-600'
                }`}>
                  {interventions.filter(i => !i.facture && i.type_activite !== 'interne').length}
                </p>
                <p className="text-xs text-red-600 font-medium">À facturer</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* FACTURÉES */}
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-900">
                  {interventions.filter(i => i.facture).length}
                </p>
                <p className="text-xs text-emerald-600">Facturées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-900">
                  {[...new Set(interventions.map(i => i.employe_nom))].length}
                </p>
                <p className="text-xs text-purple-600">Employés</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Guide Facturation pour la secrétaire */}
      {interventions.filter(i => !i.facture && i.type_activite !== 'interne').length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">
                  Guide Facturation - {interventions.filter(i => !i.facture && i.type_activite !== 'interne').length} intervention(s) à facturer
                </h3>
                <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Cliquez sur une ligne pour voir le <strong>détail de l'intervention</strong></li>
                  <li>Vérifiez le <strong>client</strong>, le <strong>type d'opération</strong> et la <strong>durée</strong></li>
                  <li>Cliquez sur le bouton <strong>"Marquer facturée"</strong> une fois la facture émise</li>
                  <li>Les interventions <span className="bg-green-200 px-1 rounded">vertes</span> sont déjà facturées</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Advanced Filters */}
      <Card className="mb-6">
        <CardHeader 
          className="py-3 cursor-pointer hover:bg-gray-50" 
          onClick={() => setShowFilters(!showFilters)}
        >
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-green-600" />
              Filtres avancés
              {Object.values(filters).some(v => v && v !== 'all') && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  Actifs
                </span>
              )}
            </span>
            {showFilters ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </CardTitle>
        </CardHeader>
        
        {showFilters && (
          <CardContent className="border-t pt-4">
            {/* Recherche globale */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Recherche globale (client, opération, matériel, commentaire...)"
                  value={filters.recherche}
                  onChange={(e) => handleFilterChange('recherche', e.target.value)}
                  className="w-full pl-10 p-2 border rounded-lg"
                />
              </div>
            </div>
            
            {/* Filtres en grille */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {/* Date début */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Date début</label>
                <input
                  type="date"
                  value={filters.dateDebut}
                  onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
              </div>
              
              {/* Date fin */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Date fin</label>
                <input
                  type="date"
                  value={filters.dateFin}
                  onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
              </div>
              
              {/* Employé */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Salarié</label>
                <select
                  value={filters.employe}
                  onChange={(e) => handleFilterChange('employe', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                >
                  <option value="">Tous</option>
                  {employes.map(e => (
                    <option key={e.id} value={e.id}>{e.nom}</option>
                  ))}
                </select>
              </div>
              
              {/* Type d'activité */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Type activité</label>
                <select
                  value={filters.typeActivite}
                  onChange={(e) => handleFilterChange('typeActivite', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                >
                  <option value="all">Tous</option>
                  <option value="agricole">Agricole (clients)</option>
                  <option value="interne">Interne</option>
                </select>
              </div>
              
              {/* Client */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Client</label>
                <input
                  type="text"
                  list="clients-list"
                  placeholder="Filtrer..."
                  value={filters.client}
                  onChange={(e) => handleFilterChange('client', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
                <datalist id="clients-list">
                  {clientsList.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              
              {/* Parcelle */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Parcelle</label>
                <input
                  type="text"
                  list="parcelles-list"
                  placeholder="Filtrer..."
                  value={filters.parcelle}
                  onChange={(e) => handleFilterChange('parcelle', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
                <datalist id="parcelles-list">
                  {parcellesList.map(p => <option key={p} value={p} />)}
                </datalist>
              </div>
              
              {/* Opération */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Opération</label>
                <input
                  type="text"
                  list="operations-list"
                  placeholder="Filtrer..."
                  value={filters.operation}
                  onChange={(e) => handleFilterChange('operation', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
                <datalist id="operations-list">
                  {operationsList.map(o => <option key={o} value={o} />)}
                </datalist>
              </div>
              
              {/* Matériel */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Matériel</label>
                <input
                  type="text"
                  list="materiels-list"
                  placeholder="Filtrer..."
                  value={filters.materiel}
                  onChange={(e) => handleFilterChange('materiel', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
                <datalist id="materiels-list">
                  {materielsList.map(m => <option key={m} value={m} />)}
                </datalist>
              </div>
              
              {/* Lieu */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block font-medium">Lieu</label>
                <select
                  value={filters.lieu}
                  onChange={(e) => handleFilterChange('lieu', e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                >
                  <option value="">Tous</option>
                  {lieuxList.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            
            {/* Boutons */}
            <div className="flex gap-2 mt-4">
              <Button onClick={applyFilters} className="bg-green-600 hover:bg-green-700">
                <Search className="h-4 w-4 mr-1" />
                Rechercher
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Main Content - Tableau des interventions */}
      <Card>
        <CardHeader className="py-3 border-b">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Interventions ({interventions.length})</span>
            <span className="text-sm font-normal text-gray-500">
              Total: {totalHeuresFiltrees.toFixed(1)} heures
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Chargement...</div>
          ) : interventions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              Aucune intervention trouvée avec ces critères
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-left p-3 font-medium text-gray-600">Opérateur</th>
                    <th className="text-left p-3 font-medium text-gray-600">Type / Opération</th>
                    <th className="text-left p-3 font-medium text-gray-600">Client</th>
                    <th className="text-left p-3 font-medium text-gray-600">Parcelle(s)</th>
                    <th className="text-left p-3 font-medium text-gray-600">Matériel utilisé</th>
                    <th className="text-right p-3 font-medium text-gray-600">Durée</th>
                    <th className="text-right p-3 font-medium text-gray-600">Tarif HT</th>
                    <th className="text-center p-3 font-medium text-gray-600">Contrat</th>
                    <th className="text-center p-3 font-medium text-gray-600">Facturation</th>
                    <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {interventions.map((int, idx) => (
                    <tr 
                      key={int.id || idx} 
                      className="hover:bg-green-50 cursor-pointer transition-colors"
                      onClick={() => openFicheIntervention(int)}
                      data-testid={`intervention-row-${int.id}`}
                    >
                      {editingId === int.id ? (
                        // Mode édition
                        <>
                          <td className="p-2">
                            <input
                              type="date"
                              value={editData.date || ''}
                              onChange={(e) => setEditData({...editData, date: e.target.value})}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-2 text-gray-500 text-xs">{int.employe_nom}</td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={editData.operation || ''}
                              onChange={(e) => setEditData({...editData, operation: e.target.value})}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={editData.client_nom || editData.lieu || ''}
                              onChange={(e) => setEditData({
                                ...editData, 
                                [editData.type_activite === 'interne' ? 'lieu' : 'client_nom']: e.target.value
                              })}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-2 text-gray-400 text-xs">-</td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={editData.materiel || ''}
                              onChange={(e) => setEditData({...editData, materiel: e.target.value})}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.5"
                              value={editData.heures_travaillees || 0}
                              onChange={(e) => setEditData({...editData, heures_travaillees: parseFloat(e.target.value)})}
                              className="w-16 p-1 border rounded text-xs text-right"
                            />
                          </td>
                          <td className="p-2 text-gray-400 text-xs text-right">-</td>
                          <td className="p-2 text-gray-400 text-xs text-center">-</td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={editData.facture || false}
                              onChange={(e) => setEditData({...editData, facture: e.target.checked})}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="p-2 text-center">
                            <div className="flex gap-1 justify-center">
                              <button onClick={saveEdit} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEdit} className="p-1 text-red-600 hover:bg-red-50 rounded">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        // Mode affichage - AMÉLIORÉ
                        <>
                          {/* QUAND - Date */}
                          <td className="p-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-blue-500" />
                              <div>
                                <div className="font-medium">{formatDate(int.date)}</div>
                                {int.heure_debut && (
                                  <div className="text-xs text-gray-400">{int.heure_debut}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* QUI - Opérateur */}
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-green-600" />
                              </div>
                              <div>
                                <span className="font-medium">{int.employe_nom || int.utilisateur || 'N/A'}</span>
                                {int.equipe && (
                                  <div className="text-xs text-gray-400">{int.equipe}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* QUOI - Type et Opération */}
                          <td className="p-3">
                            {/* Type d'intervention (culture ou type) en badge */}
                            <div className="flex flex-col gap-1">
                              {(int.type_intervention || int.culture) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 w-fit">
                                  {int.type_intervention || int.culture}
                                </span>
                              )}
                              <div className="font-semibold text-gray-800">
                                {int.operation || int.description || 'N/A'}
                              </div>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs w-fit ${
                                int.type_activite === 'interne' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {getActivityIcon(int)}
                                {int.type_activite === 'interne' ? 'Interne' : 'Prestation'}
                              </span>
                            </div>
                          </td>
                          
                          {/* CLIENT */}
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {int.type_activite === 'interne' ? (
                                <>
                                  <Home className="h-4 w-4 text-purple-500" />
                                  <span className="text-purple-600 font-medium">{int.lieu || 'Interne'}</span>
                                </>
                              ) : (
                                <>
                                  <Building className="h-4 w-4 text-amber-500" />
                                  <span className="font-medium">{int.client_nom || int.exploitation || 'N/A'}</span>
                                </>
                              )}
                            </div>
                          </td>

                          {/* PARCELLES (multi) */}
                          <td className="p-3" data-testid={`int-parcelles-${int.id}`}>
                            {(() => {
                              const parcelles = Array.isArray(int.parcelles) && int.parcelles.length > 0
                                ? int.parcelles
                                : int.parcelle_nom
                                  ? [{ nom: int.parcelle_nom, surface: int.surface_totale }]
                                  : [];
                              if (parcelles.length === 0) {
                                return <span className="text-gray-300 italic text-xs">—</span>;
                              }
                              const totalSurface = parcelles.reduce((s, p) => s + (p.surface || 0), 0);
                              return (
                                <div className="flex flex-col gap-0.5 max-w-[200px]">
                                  {parcelles.slice(0, 2).map((p, i) => (
                                    <div key={i} className="flex items-center gap-1 text-xs">
                                      <MapPin className="h-3 w-3 text-emerald-600 shrink-0" />
                                      <span className="truncate" title={p.nom}>{p.nom}</span>
                                      {p.surface > 0 && <span className="text-gray-400 shrink-0">({p.surface}ha)</span>}
                                    </div>
                                  ))}
                                  {parcelles.length > 2 && (
                                    <span className="text-[10px] text-gray-500">+{parcelles.length - 2} autres ({totalSurface.toFixed(1)}ha total)</span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          
                          {/* MATÉRIEL UTILISÉ - Amélioré avec fallbacks */}
                          <td className="p-3">
                            {(() => {
                              // Chercher le matériel dans plusieurs champs possibles
                              const materiel = int.materiel || int.machine || int.outil || int.materiel_nom;
                              const materielSecondaire = int.materiel_secondaire || int.outils;
                              
                              if (materiel) {
                                return (
                                  <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2">
                                      <Tractor className="h-4 w-4 text-green-600" />
                                      <span className="font-medium text-gray-700">{materiel}</span>
                                    </div>
                                    {materielSecondaire && Array.isArray(materielSecondaire) && materielSecondaire.length > 0 && (
                                      <div className="text-xs text-gray-400">
                                        + {materielSecondaire.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <span className="text-gray-300 italic">Non renseigné</span>
                              );
                            })()}
                          </td>
                          
                          {/* DURÉE */}
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Clock className="h-4 w-4 text-blue-400" />
                              <span className="font-bold text-blue-600">
                                {int.heures_travaillees || int.duree || 0}h
                              </span>
                            </div>
                          </td>

                          {/* TARIF HT */}
                          <td className="p-3 text-right" data-testid={`int-tarif-${int.id}`}>
                            {int.montant_ht > 0 || int.tarif_unitaire > 0 ? (
                              <div className="flex flex-col items-end">
                                <span className="font-bold text-emerald-700 flex items-center gap-1">
                                  <Euro className="h-3 w-3" />
                                  {(int.montant_ht || 0).toFixed(2)}
                                </span>
                                {int.tarif_unitaire > 0 && (
                                  <span className="text-[10px] text-gray-500">
                                    {int.tarif_unitaire}€/{int.unite || 'h'}
                                    {int.quantite_facturee > 0 && ` × ${int.quantite_facturee}`}
                                  </span>
                                )}
                              </div>
                            ) : int.type_activite === 'interne' ? (
                              <span className="text-gray-300 text-xs italic">Interne</span>
                            ) : (
                              <span className="text-amber-600 text-xs italic" title="Pas de grille tarifaire">À définir</span>
                            )}
                          </td>

                          {/* CONTRAT (lié grille tarifaire) */}
                          <td className="p-3 text-center" data-testid={`int-contrat-${int.id}`}>
                            {int.grille_id ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700" title={`Contrat ${int.grille_id}`}>
                                <FileText className="h-3 w-3" />
                                Sous contrat
                              </span>
                            ) : int.tarif_trouve === false && int.type_activite !== 'interne' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700" title="Pas de tarif trouvé">
                                <AlertCircle className="h-3 w-3" />
                                Hors contrat
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          
                          {/* FACTURÉ - 4 états cliquables */}
                          <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col items-center gap-1">
                              {(() => {
                                const statut = getStatutFacturation(int);
                                const config = STATUTS_FACTURATION[statut];
                                const Icon = config.icon;
                                return (
                                  <button
                                    onClick={(e) => toggleFacture(int, e)}
                                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 cursor-pointer ${config.color}`}
                                    title={`${config.label} - Cliquez pour changer`}
                                    data-testid={`statut-facturation-${int.id}`}
                                  >
                                    <Icon className="h-3 w-3" />
                                    {config.label}
                                  </button>
                                );
                              })()}
                              {/* Lien rapide pour marquer "Pas de facture" */}
                              {getStatutFacturation(int) !== 'pas_de_facture' && getStatutFacturation(int) !== 'facture' && (
                                <button
                                  onClick={(e) => setNonFacturable(int, e)}
                                  className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                  title="Marquer comme travail interne (pas de facture)"
                                >
                                  Interne ?
                                </button>
                              )}
                            </div>
                          </td>
                          
                          {/* ACTIONS */}
                          <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-1 justify-center">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFicheIntervention(int);
                                }}
                                className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                                title="Voir la fiche détaillée"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(int);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Modifier rapidement"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consommations carburant */}
      {consommations.length > 0 && (
        <Card className="mt-6">
          <CardHeader className="py-3 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Fuel className="h-5 w-5 text-orange-600" />
              Consommations de carburant ({consommations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-left p-3 font-medium text-gray-600">Salarié</th>
                    <th className="text-left p-3 font-medium text-gray-600">Motif</th>
                    <th className="text-right p-3 font-medium text-gray-600">Quantité</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {consommations.map((c, idx) => (
                    <tr key={c.id || idx} className="hover:bg-gray-50">
                      <td className="p-3">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                      </td>
                      <td className="p-3">{c.employe_nom || 'N/A'}</td>
                      <td className="p-3 text-gray-600">{c.motif || 'Consommation'}</td>
                      <td className="p-3 text-right font-bold text-orange-600">
                        {Math.abs(c.quantite || 0)} L
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal Fiche Intervention Détaillée */}
      <FicheInterventionModal
        open={showFicheModal}
        onClose={() => {
          setShowFicheModal(false);
          setSelectedInterventionId(null);
        }}
        interventionId={selectedInterventionId}
        onUpdate={handleFicheUpdate}
      />
    </div>
  );
}
